import { IExpressionEvaluatorContext, ObjectVariableScope } from "@@expressions";
import { useMemo } from "react";

/**
 * Creates and caches a child context with specified variable scope.
 * Optimizes performance by:
 * - Reusing the same child context instance across renders
 * - Only updating variables when their values actually change
 * 
 * **Use this hook for:**
 * - Single child context scenarios (e.g., GlobalSearch with $searchText)
 * - Container components that set variables for all children to share
 * 
 * **Don't use this hook for:**
 * - Multiple child contexts (e.g., RepeatContainer with per-item contexts)
 *   → Use Map-based caching pattern instead (see RepeatContainerComponent)
 * 
 * @param parentContext - The parent context to create a child from
 * @param variables - Record of variable names and their values to set in the child context
 * @returns Memoized child context with variables set
 * 
 * @example
 * // Single variable
 * const childContext = useChildContext(context, {
 *   [VariableNames.SearchText]: searchValue
 * });
 * 
 * @example
 * // Multiple variables
 * const childContext = useChildContext(context, {
 *   [VariableNames.Item]: item,
 *   [VariableNames.Context]: additionalData
 * });
 * 
 * @example
 * // Usage in component
 * const GlobalSearchComponent = ({ component, context }) => {
 *   const [searchValue, setSearchValue] = useState("");
 *   
 *   // All children will see $searchText
 *   const childContext = useChildContext(context, {
 *     [VariableNames.SearchText]: searchValue
 *   });
 *   
 *   return (
 *     <Box>
 *       <TextField value={searchValue} onChange={e => setSearchValue(e.target.value)} />
 *       {component.Children.map(child => (
 *         <ComponentFactory component={child} context={childContext} />
 *       ))}
 *     </Box>
 *   );
 * };
 */
export function useChildContext(
    parentContext: IExpressionEvaluatorContext,
    variables: Record<string, any>
): any {
    const childContext = useMemo(() => {
        const childVariableScope = new ObjectVariableScope(variables, parentContext.VariableScope);
        return parentContext.CreateChild(childVariableScope);
        // CreateChild is a method, not a value - omitted from deps. Depend on specific properties to avoid re-runs on object reference changes
        // parentContext.Id intentionally NOT in deps - scope replacement is detected by parentContext.VariableScope.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [parentContext.VariableScope, variables]);

    return childContext;
}
